<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template #main-filters>
        <filters
          :status-filter.sync="statusFilter"
          :scope-type-filter.sync="scopeTypeFilter"
          :request-type-filter.sync="requestTypeFilter"
          :exception-type-filter.sync="exceptionTypeFilter"
          :unit-filter.sync="unitFilter"
          :sector-ids-filter.sync="sectorIdsFilter"
          :standard-ids-filter.sync="standardIdsFilter"
          :assigned-to-filter.sync="assignedToFilter"
          :reviewed-by-filter.sync="reviewedByFilter"
          :approved-by-filter.sync="approvedByFilter"
          :created-from-filter.sync="createdFromFilter"
          :created-to-filter.sync="createdToFilter"
          :validity-from-filter.sync="validityFromFilter"
          :validity-to-filter.sync="validityToFilter"
          :status-options="statusOptions"
          :scope-type-options="scopeTypeOptions"
          :request-type-options="requestTypeOptions"
          :exception-type-options="exceptionTypeOptions"
          :unit-options="unitOptions"
          :sector-options="sectorOptions"
          :standard-options="standardOptions"
          :user-options="userOptions"
        />
      </template>
    </filter-modal>

    <b-card class="m-2 policy-exception-card" no-body>
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label class="text-capitalize">{{ $t('table.show') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label class="text-capitalize">{{ $t('table.entries') }}</label>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search') + '...'"
              />
              <b-button
                v-if="$can('create', 'PolicyException')"
                variant="secondary"
                @click="openCreate"
              >
                <span class="text-nowrap text-capitalize">
                  {{ $t('actions.add') + ' ' + $tc('policy_exception.title', 1) }}
                </span>
              </b-button>
              <b-button
                variant="success"
                class="text-capitalize ml-1"
                type="button"
                :disabled="buttonExport"
                @click="exportExcel"
              >
                <feather-icon icon="DownloadCloudIcon" size="15" />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative policy-exception-table"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head()="data">
          <span>{{ $tc(data.label, 1) }}</span>
        </template>
        <template #head(actions)="data">
          <span>{{ $tc(data.label, 2) }}</span>
        </template>

        <template #cell(status.name)="data">
          <StatusBadge :status="data.item.status" />
        </template>

        <template #cell(assignedTo.name)="data">
          {{ getAssignedToDisplay(data.item) }}
        </template>

        <template #cell(scope_type.name)="data">
          {{ getScopeTypeDisplay(data.item) }}
        </template>

        <template #cell(coverage_info)="data">
          {{ getCoverageDisplay(data.item) }}
        </template>

        <template #cell(exception_type.name)="data">
          {{ getExceptionTypeDisplay(data.item) }}
        </template>

        <template #cell(in_review_info)="data">
          {{ getInReviewDisplay(data.item) }}
        </template>
        <template #cell(approved_canceled_info)="data">
          {{ getApprovedCanceledDisplay(data.item) }}
        </template>
        <template #cell(request_date)="data">
          {{ formatDate(data.item.request_date) }}
        </template>
        <template #cell(start_date)="data">
          {{ formatDate(data.item.start_date) }}
        </template>
        <template #cell(end_date)="data">
          {{ formatDate(data.item.end_date) }}
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            boundary="viewport"
            :right="$store.state.appConfig.isRTL"
            class="policy-exception-actions-dropdown"
          >
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>
            <b-dropdown-item
              v-if="$can('update', 'PolicyException') && !isEditDisabled(data.item.status)"
              @click="goToEdit(data.item)"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('actions.edit') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="isApprovedPolicyException(data.item)"
              @click="downloadPdf(data.item.id)"
            >
              <feather-icon icon="FileIcon" />
              <span class="align-middle ml-50">{{ $t('actions.download') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canShowRenew(data.item, $can)"
              @click="onRenewFromIndex(data.item)"
            >
              <feather-icon icon="RefreshCwIcon" />
              <span class="align-middle ml-50">{{ $t('policy_exception.renew_exception') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canShowCloseException(data.item, $can)"
              @click="onOpenCloseModal(data.item)"
            >
              <feather-icon icon="XCircleIcon" />
              <span class="align-middle ml-50">{{ $t('policy_exception.close_exception') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canShowChangeAssignee(data.item, $can)"
              @click="onOpenReassignModal(data.item)"
            >
              <feather-icon icon="UserIcon" />
              <span class="align-middle ml-50">{{ $t('policy_exception.change_assignee') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="$can('cancel', 'PolicyException') && !isCanceledPolicyException(data.item)"
              @click="confirmCancel(data.item.id)"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">{{ $t('actions.cancel') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <CloseExceptionModal
        :visible="showCloseModal"
        @close="showCloseModal = false; closeActionItem = null"
        @confirm="onConfirmCloseFromIndex"
      />

      <ReassignModal
        :visible="showReassignModal"
        :user-options="reassignUserOptions"
        @close="onCloseReassignModal"
        @confirm="onConfirmReassign"
      />

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted text-capitalize">
              {{ $t('table.showing', { from: dataMeta.from, to: dataMeta.to, of: dataMeta.of }) }}
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <Pagination
              :total-list="totalList"
              :current-page="currentPage"
              :per-page="perPage"
              @updatePagination="updatePagination"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref, getCurrentInstance, onMounted } from '@vue/composition-api'
import useList from './useList'
import { isEditDisabled, getItemId, isCurrentUserTheAssignee, getLocalUserId } from './useWorkflow'
import router from '@/router'
import CloseExceptionModal from './modals/CloseExceptionModal.vue'
import ReassignModal from './modals/ReassignModal.vue'
import { fetchReassignableOptions } from './useWorkflow'
import BreadcrumbMenu from '@/components/BreadcrumbMenu.vue'
import FilterModal from '@/components/Filters/FilterModal.vue'
import ButtonFilters from '@/components/Filters/ButtonFilters.vue'
import Pagination from '@/components/Pagination.vue'
import StatusBadge from './components/StatusBadge.vue'
import Filters from './Filters.vue'
import axios from '@/libs/axios'
import Swal from 'sweetalert2'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

const safeFilename = (value) =>
  String(value || '')
    .replace(/[\/\\:*?"<>|]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()

const getPolicyExceptionStatusName = (item) => {
  if (!item) return ''
  const status = item.status
  const raw = typeof status === 'object' ? status?.name : status
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_')
}

const isApprovedPolicyException = (item) => getPolicyExceptionStatusName(item) === 'approved'

const isCanceledPolicyException = (item) => getPolicyExceptionStatusName(item) === 'canceled'

const isRequestTypeCierre = (item) => {
  if (!item) return false
  const rt = item.request_type
  const id = rt && (rt.id != null || rt.id === 0) ? rt.id : item.request_type_id
  if (id != null && Number(id) === 2) return true
  const name = (rt && rt.name) || ''
  return String(name).toLowerCase().trim() === 'cierre'
}

const isClosedOrCanceled = (item) => {
  const status = getPolicyExceptionStatusName(item)
  return status === 'closed' || status === 'canceled'
}

const STATUSES_ALLOWED_FOR_CHANGE_ASSIGNEE = ['in_progress', 'in_review', 'reassigned', 'approved']

const isStatusAllowedForSpecialButtons = (item) => {
  const status = getPolicyExceptionStatusName(item)
  return STATUSES_ALLOWED_FOR_CHANGE_ASSIGNEE.includes(status)
}

const hasSpecialPermissionShowSpecialButtons = ($can) =>
  $can && typeof $can === 'function' && $can('special_permission_show_special_buttons', 'PolicyException')

const canShowCloseException = (item, $can = null) => {
  if (!item) return false
  if (!isApprovedPolicyException(item)) return false
  if (isClosedOrCanceled(item)) return false
  if (isRequestTypeCierre(item)) return false
  return isCurrentUserTheAssignee(item) || hasSpecialPermissionShowSpecialButtons($can)
}

const canShowRenew = (item, $can = null) =>
  isApprovedPolicyException(item) &&
  (isCurrentUserTheAssignee(item) || hasSpecialPermissionShowSpecialButtons($can))

const canShowChangeAssignee = (item, $can = null) =>
  isStatusAllowedForSpecialButtons(item) &&
  (isCurrentUserTheAssignee(item) || hasSpecialPermissionShowSpecialButtons($can))

export default {
  components: {
    BreadcrumbMenu,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    vSelect,
    FilterModal,
    ButtonFilters,
    StatusBadge,
    CloseExceptionModal,
    ReassignModal,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const toast = useToast()
    const {
      fetchList,
      tableColumns,
      getAssignedToDisplay,
      getInReviewDisplay,
      getApprovedCanceledDisplay,
      getCoverageDisplay,
      getScopeTypeDisplay,
      getExceptionTypeDisplay,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDate,
      statusFilter,
      scopeTypeFilter,
      requestTypeFilter,
      unitFilter,
      sectorIdsFilter,
      exceptionTypeFilter,
      standardIdsFilter,
      assignedToFilter,
      reviewedByFilter,
      approvedByFilter,
      createdFromFilter,
      createdToFilter,
      validityFromFilter,
      validityToFilter,
      statusOptions,
      scopeTypeOptions,
      requestTypeOptions,
      exceptionTypeOptions,
      unitOptions,
      sectorOptions,
      standardOptions,
      userOptions,
      filtersCount,
      buildFilters,
      clearSearchFilters,
    } = useList()

    onMounted(async () => {
      await store.dispatch('policyException/fetchCatalogs')
      await store.dispatch('policyException/fetchStandards')
      await store.dispatch('unit/getAll')
      await store.dispatch('region/getAll')
      await store.dispatch('sector/getAll')
    })

    const showCloseModal = ref(false)
    const closeActionItem = ref(null)
    const showReassignModal = ref(false)
    const reassignItem = ref(null)
    const reassignUserOptions = ref([])
    const actionLoading = ref(false)

    const findRenovacionRequestTypeId = (options) => {
      if (!Array.isArray(options) || options.length === 0) return null
      const found = options.find(
        (r) =>
          String(r.name || '').toLowerCase().includes('renovacion') ||
          String(r.name || '').toLowerCase().includes('renovación') ||
          String(r.name || '').toLowerCase().includes('renewal')
      )
      return found && (found.id != null || found.value != null) ? (found.id ?? found.value) : null
    }

    const onRenewFromIndex = async (item) => {
      const id = getItemId(item)
      if (id == null) return
      const renovacionId = findRenovacionRequestTypeId(requestTypeOptions.value)
      if (renovacionId == null) {
        toast({
          component: ToastificationContent,
          props: {
            title: vm.$t('policy_exception.unable_to_load'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }
      actionLoading.value = true
      try {
        await store.dispatch('policyException/patch', {
          id,
          payload: { request_type_id: renovacionId },
        })
        await store.dispatch('policyException/updateStatus', {
          id,
          payload: { status: 'in_progress' },
        })
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: vm.$t('policy_exception.renew_exception') || 'Excepción renovada',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        goToEdit(item)
      } catch (err) {
        const errData = err && err.response && err.response.data ? err.response.data : {}
        toast({
          component: ToastificationContent,
          props: {
            title: (errData && errData.message) || vm.$t('errors.generic'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        actionLoading.value = false
      }
    }

    const REQUEST_TYPE_CIERRE_ID = 2

    const onOpenCloseModal = (item) => {
      if (getItemId(item) == null) return
      closeActionItem.value = item
      showCloseModal.value = true
    }

    const onConfirmCloseFromIndex = async ({ close_reason: reason }) => {
      const item = closeActionItem.value
      const id = item ? getItemId(item) : null
      if (!id || !reason || !reason.trim()) return
      const currentUserId = getLocalUserId()
      if (currentUserId == null || currentUserId === '') {
        toast({
          component: ToastificationContent,
          props: {
            title: vm.$t('policy_exception.unable_to_load') || 'Usuario no identificado',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }
      actionLoading.value = true
      try {
        await store.dispatch('policyException/patch', {
          id,
          payload: {
            request_type_id: REQUEST_TYPE_CIERRE_ID,
            comments: reason.trim(),
          },
        })
        await store.dispatch('policyException/updateStatus', {
          id,
          payload: { status: 'in_progress', id_user: currentUserId },
        })
        showCloseModal.value = false
        closeActionItem.value = null
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: vm.$t('policy_exception.close_exception') || 'Excepción cerrada',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        if (item) goToEdit(item)
      } catch (err) {
        const errData = err && err.response && err.response.data ? err.response.data : {}
        toast({
          component: ToastificationContent,
          props: {
            title: (errData && errData.message) || vm.$t('errors.generic'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        actionLoading.value = false
      }
    }

    const onOpenReassignModal = async (item) => {
      reassignItem.value = item
      showReassignModal.value = true
      actionLoading.value = true
      try {
        const options = await fetchReassignableOptions()
        reassignUserOptions.value = Array.isArray(options) ? options : []
      } catch (_) {
        reassignUserOptions.value = []
      } finally {
        actionLoading.value = false
      }
    }

    const onCloseReassignModal = () => {
      showReassignModal.value = false
      reassignItem.value = null
    }

    const onConfirmReassign = async ({ id_user }) => {
      const item = reassignItem.value
      const id = getItemId(item)
      if (id == null || id_user == null) return
      actionLoading.value = true
      try {
        await store.dispatch('policyException/updateStatus', {
          id,
          payload: { status: 'reassigned', id_user },
        })
        showReassignModal.value = false
        reassignItem.value = null
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: vm.$t('policy_exception.change_assignee') || 'Asignado actualizado',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (err) {
        const errData = err && err.response && err.response.data ? err.response.data : {}
        toast({
          component: ToastificationContent,
          props: {
            title: (errData && errData.message) || vm.$t('errors.generic'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        actionLoading.value = false
      }
    }

    const updatePagination = (page) => {
      const nextPage = Number(page)
      if (Number.isNaN(nextPage) || nextPage < 1) return
      currentPage.value = nextPage
      refetchData()
    }

    const openCreate = () => {
      router.push({ name: 'policyExceptionCreate' })
    }

    const goToEdit = (item) => {
      const id = getItemId(item)
      if (id == null) return
      router.push({ name: 'policyExceptionEdit', params: { id } })
    }

    const downloadPdfPolicyException = async (id) => {
      const response = await axios.get(`/v1/policy-exceptions/${id}`, {
        params: { is_pdf: true },
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      })
      const cd = response.headers && response.headers['content-disposition']
      const fallback = `policy_exception_${id}.pdf`
      let filename = fallback
      if (cd) {
        const match = String(cd).match(/filename=\"?([^\";]+)\"?/i)
        if (match) {
          try {
            filename = decodeURIComponent(match[1])
          } catch (_) {
            filename = match[1]
          }
        }
      }
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = safeFilename(filename || fallback)
      a.click()
      window.URL.revokeObjectURL(blobUrl)
    }

    const downloadPdf = async (id) => {
      try {
        await downloadPdfPolicyException(id)
      } catch (_) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'No se pudo descargar',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }

    const confirmCancel = (id) => {
      Swal.fire({
        title: vm.$t('policy_exception.workflow.cancel_title') || '¿Cancelar?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: vm.$t('actions.cancel') || 'Cancelar',
        cancelButtonText: vm.$t('actions.exit') || 'Salir',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          store.dispatch('policyException/updateStatus', { id, payload: { status: 'canceled' } }).then(() => {
            refetchData()
            toast({
              component: ToastificationContent,
              props: {
                title: vm.$t('policy_exception.status_canceled') || 'Excepción cancelada',
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          }).catch(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: vm.$t('errors.generic') || 'Error al cancelar',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
        }
      })
    }

    const buttonExport = ref(false)
    const exportExcel = async () => {
      try {
        buttonExport.value = true
        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          sort_by: sortBy.value,
          sort_desc: isSortDirDesc.value ? 1 : 0,
          search: searchQuery.value || undefined,
          status_id: statusFilter.value || undefined,
          scope_type_id: scopeTypeFilter.value || undefined,
          request_type_id: requestTypeFilter.value || undefined,
          action: 'export',
        }
        const response = await axios.get('/v1/policy-exceptions', { params, responseType: 'blob' })
        const cd = response.headers && response.headers['content-disposition']
        let filename = 'policy_exceptions.xlsx'
        if (cd) {
          const match = String(cd).match(/filename=\"?([^\";]+)\"?/i)
          if (match) {
            try {
              filename = decodeURIComponent(match[1])
            } catch (_) {
              filename = match[1]
            }
          }
        }
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = safeFilename(filename)
        a.click()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: (error && error.response && error.response.data && error.response.data.message) || 'Error al exportar',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        buttonExport.value = false
      }
    }

    return {
      fetchList,
      tableColumns,
      getAssignedToDisplay,
      getInReviewDisplay,
      getApprovedCanceledDisplay,
      getCoverageDisplay,
      getScopeTypeDisplay,
      getExceptionTypeDisplay,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDate,
      statusFilter,
      scopeTypeFilter,
      requestTypeFilter,
      unitFilter,
      sectorIdsFilter,
      exceptionTypeFilter,
      standardIdsFilter,
      assignedToFilter,
      reviewedByFilter,
      approvedByFilter,
      createdFromFilter,
      createdToFilter,
      validityFromFilter,
      validityToFilter,
      statusOptions,
      scopeTypeOptions,
      requestTypeOptions,
      exceptionTypeOptions,
      unitOptions,
      sectorOptions,
      standardOptions,
      userOptions,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      updatePagination,
      openCreate,
      goToEdit,
      isEditDisabled,
      isApprovedPolicyException,
      isCanceledPolicyException,
      isRequestTypeCierre,
      canShowCloseException,
      canShowRenew,
      canShowChangeAssignee,
      confirmCancel,
      downloadPdf,
      exportExcel,
      buttonExport,
      showCloseModal,
      closeActionItem,
      onRenewFromIndex,
      onOpenCloseModal,
      onConfirmCloseFromIndex,
      showReassignModal,
      reassignItem,
      reassignUserOptions,
      onOpenReassignModal,
      onCloseReassignModal,
      onConfirmReassign,
    }
  },
}
</script>

<style scoped>
.policy-exception-actions-dropdown ::v-deep .dropdown-menu {
  min-width: 11rem;
}

.policy-exception-card {
  overflow: visible;
}

.policy-exception-table ::v-deep .table-responsive,
.policy-exception-table ::v-deep table {
  overflow: visible;
}
</style>
